import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import ChangeTag from "common/Tags/ChangeTag";
import { typography } from "@shared/common/Themes/typography";
import Flex from "common/Boxes/Flex";
import React from "react";
import useStatistics from "@shared/features/bots/BotForm/Backtesting/useStatistics";
import useLiveBacktesting from "@shared/features/bots/BotForm/Backtesting/useLiveBacktesting";
import { BacktestingRunType } from "@shared/types";
import { BacktestingGeneralStatistics } from "@shared/features/bots/BotForm/common/types";
import { formatStatisticAmount } from "features/bots/BotForm/NewLook/format";
import styled, { useTheme } from "styled-components";
import PriceChange from "common/Charts/PriceChange";

interface PnlComponentProps {
  backtestingRun?: BacktestingRunType;
  pnlType:
    | BacktestingGeneralStatistics.REALIZED_PNL
    | BacktestingGeneralStatistics.NET_PNL;
}

const PnlComponent = ({ backtestingRun, pnlType }: PnlComponentProps) => {
  const theme = useTheme();
  const { liveBacktestingData } = useLiveBacktesting({ data: backtestingRun });
  const { titleToValue, getChangeValue, getDisplayedValue } = useStatistics({
    backtestingRun: liveBacktestingData,
  });
  const pnlValue = titleToValue[pnlType];

  if (theme.isNewLook) {
    const numericValue = Number(pnlValue) || 0;
    const sign = numericValue > 0 ? "+" : numericValue < 0 ? "−" : "";
    return (
      <NewLookWrapper>
        <NewLookAmount $value={numericValue}>
          {sign}
          <TextWithIcon
            text={formatStatisticAmount(
              Math.abs(numericValue),
              getDisplayedValue
            )}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
            style={{ color: "inherit" }}
          />
        </NewLookAmount>
        <PriceChange
          change={Number(getChangeValue(pnlType)) || 0}
          size={12}
          useBackground={true}
          style={CHANGE_STYLE}
        />
      </NewLookWrapper>
    );
  }

  return (
    <Flex $justifyContent="flex-start">
      <TextWithIcon
        text={getDisplayedValue(pnlValue)}
        iconType={ICON_TYPES.DISPLAY_CURRENCY}
        style={{ color: theme.colors.primaryGrey }}
      />
      <ChangeTag
        value={getChangeValue(pnlType)}
        suffix="%"
        style={{ fontSize: typography.fontSizes.S, lineHeight: "20px" }}
        arrowStyle={{ fontSize: 8 }}
      />
    </Flex>
  );
};

export default PnlComponent;

const CHANGE_STYLE = { fontWeight: 500 };
const NewLookWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;
const NewLookAmount = styled.span<{ $value: number }>`
  display: inline-flex;
  align-items: center;
  font-variant-numeric: tabular-nums;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) =>
    p.$value > 0
      ? p.theme.colors.green
      : p.$value < 0
      ? p.theme.colors.red
      : p.theme.colors.primaryText};
`;
